import * as React from 'react'

import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { Checkbox, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material'

import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { SmartSelect } from '../generic/smartSelect'

/**
* @typedef {import("../generic/utilities/objectStructures/planner/planification").Settings} Settings Planification Settings object
*/

/**
 * Returns the display label for a unit criteria option.
 * Maps the internal value "Job" to the i18n key "Part" (RN1 / CA1).
 * All other values are returned unchanged.
 * @param {string} option Internal option value from planificationCriteria endpoint
 * @param {function} t_ Translation function
 * @returns {string} Visible label for the option
 */
export const getUnitCriteriaLabel = (option, t_) => {
  if (option === 'Job') return t_('Part')
  return String(option ?? '')
}

/**
 * @param {object} props Props object
 * @param {Settings} props.planParameters Plan parameters object
 * @param {React.Dispatch<React.SetStateAction<Settings>>} props.setPlanParameters Plan parameters useState setter
  @param {string[]} props.planificationCriteriaTypeList Planification criteria available type list
 * @param {boolean} props.disableUnitSelector If true, unit selector will be disabled
 * @returns {JSX.Element} PlanDialogContent component
 */
export default function PlanDialogContent (
  {
    setPlanParameters,
    planParameters,
    planificationCriteriaTypeList,
    disableUnitSelector
  }) {
  const { t_ } = useSmartTranslation()
  return (
    <Stack justifyContent='space-around' direction='row' sx={{ marginTop: '1rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', width: 'max-content' }}>
        <div style={{ gridColumn: 'span 2' }}>
          <SmartSelect
            disabled={disableUnitSelector}
            value={planParameters.unitCriteria}
            selectableOptions={planificationCriteriaTypeList}
            onChange={(newValue) => setPlanParameters({ ...planParameters, unitCriteria: newValue })}
            label={t_('Select planning unit')}
            variant='standard'
            optionLabelTransform={(option) => getUnitCriteriaLabel(option, t_)}
          />
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <TextField
            autoFocus
            fullWidth
            margin='dense'
            id='name'
            label={t_('Execution time')}
            variant='standard'
            value={planParameters.time_exec}
            error={planParameters.showErrors && (planParameters.time_exec === '' || parseInt(planParameters.time_exec) < 1)}
            helperText={planParameters.showErrors && (planParameters.showErrors && (planParameters.time_exec === '' || parseInt(planParameters.time_exec) < 1)) ? 'Value must not be lower than 1 ' : null}
            onChange={e => {
              setPlanParameters(prevState => ({
                ...prevState,
                time_exec: e.target.value
              }))
            }}
          />
        </div>
        <FormControl fullWidth variant='filled'>
          <InputLabel id='demo-simple-select-label'>{t_('Temporal criteria')}</InputLabel>
          <Select
            color='primary'
            labelId='demo-simple-select-label'
            id='demo-simple-select'
            label='Criterio temporal'
            onChange={e => {
              setPlanParameters(prevState => ({
                ...prevState,
                criteria: e.target.value
              }))
            }}
            value={planParameters.criteria}
          >
            <MenuItem value='asap'>ASAP</MenuItem>
            <MenuItem value='jit'>JIT</MenuItem>
          </Select>
        </FormControl>
        <TextField
          autoFocus
          margin='dense'
          id='name'
          label={t_('Priority')}
          fullWidth
          variant='standard'
          value={planParameters.priotemp}
          error={planParameters.showErrors && (planParameters.priotemp === '' || parseInt(planParameters.priotemp) < 1 || parseInt(planParameters.priotemp) > 10)}
          helperText={planParameters.showErrors && (planParameters.priotemp === '' || parseInt(planParameters.priotemp) < 1 || parseInt(planParameters.priotemp) > 10) ? 'Value must be between 1 and 10' : null}
          onChange={e => {
            setPlanParameters(prevState => ({
              ...prevState,
              priotemp: e.target.value
            }))
          }}
        />
        <FormControl fullWidth variant='filled'>
          <InputLabel id='demo-simple-select-label'>{t_('Cost criteria')}</InputLabel>
          <Select
            labelId='demo-simple-select-label'
            id='demo-simple-select'
            label='Cost criteria'
            value={planParameters.cost_criteria}
            onChange={e => {
              setPlanParameters(prevState => ({
                ...prevState,
                cost_criteria: e.target.value
              }))
            }}
          >
            <MenuItem value='cost'>{t_('Cost')}</MenuItem>
            <MenuItem value='energy'>{t_('Energy consumption')}</MenuItem>
            <MenuItem value='defective'>{t_('Defective')}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          autoFocus
          margin='dense'
          id='name'
          label={t_('Priority')}
          fullWidth
          variant='standard'
          error={planParameters.showErrors && (planParameters.priocost === '' || parseInt(planParameters.priocost) < 1 || parseInt(planParameters.priocost) > 10)}
          helperText={planParameters.showErrors && (planParameters.priocost === '' || parseInt(planParameters.priocost) < 1 || parseInt(planParameters.priocost) > 10) ? 'Value must be between 1 and 10' : null}
          value={planParameters.priocost}
          onChange={e => {
            setPlanParameters(prevState => ({
              ...prevState,
              priocost: e.target.value
            }))
          }}
        />

        <FormControlLabel
          sx={{ gridColumn: 'span 2' }}
          control={
            <Checkbox
              checked={planParameters.foForecast}
              onChange={(event) => setPlanParameters({ ...planParameters, foForecast: event.target.checked })}
            />
            }
          label={t_('Fabrication order forecast')}
        />
        <TextField
          value={planParameters.temporalHorizon}
          InputLabelProps={{ shrink: true }}
          label={t_('Time Horizon')}
          onChange={(event) => {
            if (isNaN(event.target.value)) {
              return
            }
            if (event.target.value === '' || event.target.value === null) {
              setPlanParameters({ ...planParameters, temporalHorizon: '', temporalHorizonUnit: null })
              return
            }
            if (event.target.value < 0) {
              setPlanParameters({ ...planParameters, temporalHorizon: 0 })
              return
            }

            if (planParameters.temporalHorizonUnit === null) {
              setPlanParameters({ ...planParameters, temporalHorizon: event.target.value, temporalHorizonUnit: 'MONTH' })
              return
            }
            setPlanParameters({ ...planParameters, temporalHorizon: event.target.value })
          }}
          sx={{ alignSelf: 'center' }}
        />
        <RadioGroup
          name='radio-buttons-group'
          value={planParameters.temporalHorizonUnit}
          onChange={(event) => {
            if (planParameters.temporalHorizon === null || planParameters.temporalHorizon === '') {
              return
            }
            setPlanParameters({ ...planParameters, temporalHorizonUnit: event.target.value })
          }}
        >
          <FormControlLabel value='DAY' control={<Radio />} label={t_('Days')} />
          <FormControlLabel value='WEEK' control={<Radio />} label={t_('Weeks')} />
          <FormControlLabel value='MONTH' control={<Radio />} label={t_('Months')} />
        </RadioGroup>
      </div>
    </Stack>
  )
}
